/* eslint-disable no-unused-vars */
// =============================================================================
// TargetingSettingsScreen.js — FULL REBUILD
// JobEng — Business / Marketing
// Location: app/screens/business/marketing/TargetingSettingsScreen.js
//
// Saves audience targeting as JSON into campaigns.targeting via
// PUT /api/data/campaigns/:id  (run the ALTER below if the column is missing)
// DB monitor: /health + campaigns table ping badge
// Features: demographics · locations (custom add) · radius · professional
//   (job titles, industries, company size, experience, salary band) ·
//   education · interests · advanced (exclusions, languages, custom audience)
//   · live estimated-reach gauge · AI-suggested targeting from campaign
//   context · reset to defaults · loads existing targeting (string or object)
// =============================================================================

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  ScrollView,
  TextInput,
  Image,
  ActivityIndicator,
  Alert,
  Platform,
  KeyboardAvoidingView,
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { Ionicons, MaterialCommunityIcons } from '@expo/vector-icons';
import AsyncStorage from '@react-native-async-storage/async-storage';

// ─── API ─────────────────────────────────────────────────────────────────────

const API_BASE = 'https://api.jobeng.ai';
const API_KEY =
  '16a0651c78da416445e9c2946fb72fd9a6e4ce4a71ba610db15251050c3462ef';

const getH = async () => {
  const tok =
    (await AsyncStorage.getItem('auth_token').catch(() => null)) ||
    (await AsyncStorage.getItem('userToken').catch(() => null));
  return {
    'Content-Type': 'application/json',
    'x-api-key': API_KEY,
    ...(tok ? { Authorization: `Bearer ${tok}` } : {}),
  };
};

// ─── Constants ───────────────────────────────────────────────────────────────

const C = {
  primary: '#0e4e96',
  primaryDark: '#093a72',
  primaryLight: '#1e7be2',
  accent: '#EC4899',
  bg: '#F4F6FB',
  card: '#FFFFFF',
  text: '#0F172A',
  sub: '#64748B',
  border: '#E2E8F0',
  success: '#10B981',
  warning: '#F59E0B',
  danger: '#EF4444',
  purple: '#7C3AED',
};

const AGE_RANGES = ['18-24', '25-34', '35-44', '45-54', '55-64', '65+'];
const GENDERS = [
  { id: 'all', label: 'All' },
  { id: 'male', label: 'Male' },
  { id: 'female', label: 'Female' },
];
const COUNTRIES = ['United Kingdom', 'Ireland', 'United States', 'Canada', 'Australia', 'Germany', 'France', 'Netherlands', 'UAE'];
const UK_CITIES = ['London', 'Manchester', 'Birmingham', 'Leeds', 'Glasgow', 'Edinburgh', 'Bristol', 'Liverpool', 'Newcastle', 'Sheffield', 'Cardiff', 'Belfast'];
const COMPANY_SIZES = ['1-10', '11-50', '51-200', '201-500', '501-1000', '1001+'];
const EDUCATION_LEVELS = ['GCSE / A-Level', 'Apprenticeship', 'HNC / HND', 'Bachelor', 'Master', 'PhD'];
const EXPERIENCE_BANDS = ['0-2 yrs', '3-5 yrs', '6-10 yrs', '10+ yrs'];
const SALARY_BANDS = ['Up to £25k', '£25k-£40k', '£40k-£60k', '£60k-£90k', '£90k+'];
const LANGUAGES = ['English', 'Polish', 'Romanian', 'Spanish', 'French', 'Arabic', 'Urdu', 'Portuguese'];
const SUGGESTED_INTERESTS = ['Technology', 'Innovation', 'Engineering Projects', 'Career Development', 'Construction', 'Sustainability', 'Management', 'Startups'];

const DEFAULT_TARGETING = {
  ageRanges: ['25-34', '35-44'],
  gender: 'all',
  countries: ['United Kingdom'],
  cities: ['London', 'Manchester', 'Birmingham'],
  radius: 50,
  jobTitles: ['Engineer', 'Project Manager', 'Technician'],
  industries: ['Engineering', 'Construction', 'Manufacturing'],
  companySize: ['11-50', '51-200', '201-500'],
  experience: ['3-5 yrs', '6-10 yrs'],
  salaryBands: [],
  educationLevel: ['Bachelor', 'Master'],
  fieldOfStudy: ['Engineering', 'Computer Science'],
  interests: ['Technology', 'Engineering Projects'],
  languages: ['English'],
  excludeAudience: [],
  customAudience: false,
};

const parseTargeting = (raw) => {
  if (!raw) return null;
  if (typeof raw === 'object') return raw;
  if (typeof raw === 'string') { try { return JSON.parse(raw); } catch { return null; } }
  return null;
};

// =============================================================================
// MODULE-SCOPE COMPONENTS (React.memo)
// =============================================================================

const DbBadge = React.memo(({ status, onPress }) => {
  const cfg =
    status === 'online' ? { c: '#34D399', t: 'Live' }
    : status === 'offline' ? { c: '#F87171', t: 'Offline' }
    : { c: '#FBBF24', t: '…' };
  return (
    <TouchableOpacity style={s.dbBadge} onPress={onPress} activeOpacity={0.7}>
      <View style={[s.dbDot, { backgroundColor: cfg.c }]} />
      <Text style={s.dbText}>{cfg.t}</Text>
    </TouchableOpacity>
  );
});

const CompanyLogo = React.memo(({ uri, size = 28 }) => {
  if (uri && typeof uri === 'string' && (uri.startsWith('http') || uri.startsWith('data:'))) {
    return <Image source={{ uri }} style={{ width: size, height: size, borderRadius: 7 }} resizeMode="contain" onError={() => {}} />;
  }
  return (
    <View style={{ width: size, height: size, borderRadius: 7, backgroundColor: 'rgba(255,255,255,0.2)', alignItems: 'center', justifyContent: 'center' }}>
      <MaterialCommunityIcons name="office-building" size={size * 0.6} color="#fff" />
    </View>
  );
});

const Section = React.memo(({ icon, title, subtitle, children, count }) => (
  <View style={s.section}>
    <View style={s.sectionHead}>
      <View style={s.sectionIcon}>
        <MaterialCommunityIcons name={icon} size={17} color={C.primary} />
      </View>
    <View style={{ flex: 1, marginLeft: 10 }}>
        <Text style={s.sectionTitle}>{title}</Text>
        {!!subtitle && <Text style={s.sectionSub}>{subtitle}</Text>}
      </View>
      {count !== undefined && (
        <View style={s.countBadge}><Text style={s.countText}>{count}</Text></View>
      )}
    </View>
    {children}
  </View>
));

const Chip = React.memo(({ label, active, onPress, removable, onRemove }) => (
  <TouchableOpacity
    style={[s.chip, active && s.chipActive]}
    onPress={removable ? onRemove : onPress}
    activeOpacity={0.7}
  >
    <Text style={[s.chipText, active && s.chipTextActive]}>{label}</Text>
    {removable && <Ionicons name="close" size={12} color={active ? '#fff' : C.sub} style={{ marginLeft: 4 }} />}
  </TouchableOpacity>
));

const AddRow = React.memo(({ value, onChangeText, onAdd, placeholder }) => (
  <View style={s.addRow}>
    <TextInput
      style={s.addInput}
      value={value}
      onChangeText={onChangeText}
      placeholder={placeholder}
      placeholderTextColor="#9CA3AF"
      onSubmitEditing={onAdd}
      returnKeyType="done"
    />
    <TouchableOpacity style={s.addBtn} onPress={onAdd}>
      <Ionicons name="add" size={18} color="#fff" />
    </TouchableOpacity>
  </View>
));

const Stepper = React.memo(({ label, value, onChange, min = 5, max = 500, step = 5, suffix = ' km' }) => (
  <View style={s.stepperRow}>
    <Text style={s.stepperLabel}>{label}</Text>
    <View style={s.stepperControls}>
      <TouchableOpacity style={s.stepperBtn} onPress={() => onChange(Math.max(min, value - step))}>
        <Ionicons name="remove" size={16} color={C.primary} />
      </TouchableOpacity>
      <Text style={s.stepperValue}>{value}{suffix}</Text>
      <TouchableOpacity style={s.stepperBtn} onPress={() => onChange(Math.min(max, value + step))}>
        <Ionicons name="add" size={16} color={C.primary} />
      </TouchableOpacity>
    </View>
  </View>
));

const Toggle = React.memo(({ label, sub, value, onChange }) => (
  <TouchableOpacity style={s.toggleRow} onPress={() => onChange(!value)} activeOpacity={0.7}>
    <View style={{ flex: 1, marginRight: 10 }}>
      <Text style={s.stepperLabel}>{label}</Text>
      {!!sub && <Text style={s.toggleSub}>{sub}</Text>}
    </View>
    <View style={[s.toggleTrack, value && { backgroundColor: C.success }]}>
      <View style={[s.toggleThumb, value && { alignSelf: 'flex-end' }]} />
    </View>
  </TouchableOpacity>
));

// =============================================================================
// MAIN SCREEN
// =============================================================================

const TargetingSettingsScreen = ({ navigation, route }) => {
  const { campaign: campaignParam, campaignId: campaignIdParam } = route?.params || {};
  const campaign = campaignParam || {};
  const campaignId =
    campaignIdParam || campaign?.id || campaign?.campaign_id || campaign?._raw?.id || null;

  const [companyLogo, setCompanyLogo] = useState(null);
  const [t, setT] = useState(() => ({
    ...DEFAULT_TARGETING,
    ...(parseTargeting(campaign?.targeting) || {}),
  }));

  const [jobTitleInput, setJobTitleInput] = useState('');
  const [industryInput, setIndustryInput] = useState('');
  const [cityInput, setCityInput] = useState('');
  const [fieldInput, setFieldInput] = useState('');
  const [interestInput, setInterestInput] = useState('');
  const [excludeInput, setExcludeInput] = useState('');

  const [saving, setSaving] = useState(false);
  const [aiBusy, setAiBusy] = useState(false);
  const [dirty, setDirty] = useState(false);

  const [dbStatus, setDbStatus] = useState('checking');
  const [dbDetail, setDbDetail] = useState('');

  // ---------------------------------------------------------------------------
  // SETUP + DB CHECK
  // ---------------------------------------------------------------------------

  useEffect(() => {
    AsyncStorage.getItem('company_profile').then((raw) => {
      if (!raw) return;
      try {
        const p = JSON.parse(raw);
        const logo = p.logo || p.logo_url || p.company_logo;
        if (logo && typeof logo === 'string') {
          if (logo.startsWith('http') || logo.startsWith('data:')) setCompanyLogo(logo);
          else if (logo.startsWith('/')) setCompanyLogo(API_BASE + logo);
        }
      } catch {}
    }).catch(() => {});
    checkDb();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const checkDb = useCallback(async () => {
    setDbStatus('checking');
    try {
      const h = await getH();
      const t0 = Date.now();
      const health = await fetch(`${API_BASE}/health`, { headers: h }).then((r) => r.ok).catch(() => false);
      const table = await fetch(`${API_BASE}/api/data/campaigns?limit=1`, { headers: h }).then((r) => r.ok).catch(() => false);
      const ms = Date.now() - t0;
      if (health && table) { setDbStatus('online'); setDbDetail(`API + campaigns table reachable · ${ms}ms`); }
      else if (health) { setDbStatus('offline'); setDbDetail('API up, campaigns table unreachable'); }
      else { setDbStatus('offline'); setDbDetail('API not responding — check PM2 / nginx'); }
    } catch (e) { setDbStatus('offline'); setDbDetail(e?.message || 'Network error'); }
  }, []);

  const showDbDetail = useCallback(() => {
    Alert.alert(
      dbStatus === 'online' ? '✅ Connected' : '⚠️ Connection issue',
      dbDetail || 'Checking…',
      [{ text: 'Re-check', onPress: checkDb }, { text: 'OK' }]
    );
  }, [dbStatus, dbDetail, checkDb]);

  // ---------------------------------------------------------------------------
  // STATE HELPERS
  // ---------------------------------------------------------------------------

  const set = useCallback((patch) => { setT((prev) => ({ ...prev, ...patch })); setDirty(true); }, []);

  const toggleIn = useCallback((key, value) => {
    setT((prev) => {
      const arr = Array.isArray(prev[key]) ? prev[key] : [];
      return { ...prev, [key]: arr.includes(value) ? arr.filter((x) => x !== value) : [...arr, value] };
    });
    setDirty(true);
  }, []);

  const addTo = useCallback((key, value, clear) => {
    const v = value.trim();
    if (!v) return;
    setT((prev) => {
      const arr = Array.isArray(prev[key]) ? prev[key] : [];
      if (arr.some((x) => x.toLowerCase() === v.toLowerCase())) return prev;
      return { ...prev, [key]: [...arr, v] };
    });
    clear('');
    setDirty(true);
  }, []);

  const removeFrom = useCallback((key, value) => {
    setT((prev) => ({ ...prev, [key]: (prev[key] || []).filter((x) => x !== value) }));
    setDirty(true);
  }, []);

  // ---------------------------------------------------------------------------
  // ESTIMATED REACH (heuristic — broader selections = larger audience)
  // ---------------------------------------------------------------------------

  const estimate = useMemo(() => {
    let base = 1200000; // UK professional pool baseline
    base *= Math.min(1, (t.countries?.length || 1) * 0.55 + 0.45);
    base *= Math.min(1, 0.18 + (t.cities?.length || 0) * 0.09);
    base *= Math.min(1, 0.25 + (t.ageRanges?.length || 0) * 0.16);
    if (t.gender !== 'all') base *= 0.5;
    base *= Math.min(1, 0.3 + (t.jobTitles?.length || 0) * 0.1);
    base *= Math.min(1, 0.35 + (t.industries?.length || 0) * 0.12);
    base *= Math.min(1, 0.4 + (t.educationLevel?.length || 0) * 0.12);
    base *= Math.max(0.55, 1 - (t.excludeAudience?.length || 0) * 0.08);
    const n = Math.max(800, Math.round(base));
    const label = n >= 1000000 ? `${(n / 1000000).toFixed(1)}M` : n >= 1000 ? `${Math.round(n / 1000)}K` : String(n);
    const ratio = Math.min(1, n / 1200000);
    const band = ratio > 0.55 ? { txt: 'Broad', color: C.warning } : ratio > 0.12 ? { txt: 'Balanced', color: C.success } : { txt: 'Narrow', color: C.danger };
    return { label, ratio, band };
  }, [t]);

  // ---------------------------------------------------------------------------
  // AI SUGGEST
  // ---------------------------------------------------------------------------

  const aiSuggest = useCallback(async () => {
    setAiBusy(true);
    try {
      const h = await getH();
      const prompt =
        `Suggest audience targeting for a UK marketing campaign` +
        (campaign?.name ? ` called "${campaign.name}"` : '') +
        (campaign?.objective ? ` with objective "${campaign.objective}"` : '') +
        (campaign?.target_audience ? ` aimed at: ${campaign.target_audience}` : '') +
        `. Respond ONLY with JSON, no markdown, no preamble, using exactly these keys: ` +
        `{"ageRanges":["25-34"],"jobTitles":[],"industries":[],"interests":[],"cities":[],"educationLevel":[],"experience":[]}` +
        ` Choose from age ranges ${AGE_RANGES.join('/')}, UK cities, education levels ${EDUCATION_LEVELS.join('/')}, experience bands ${EXPERIENCE_BANDS.join('/')}. Max 5 items per list.`;
      const res = await fetch(`${API_BASE}/api/ai`, {
        method: 'POST', headers: h, body: JSON.stringify({ prompt }),
      }).then((r) => r.json()).catch(() => null);
      const content = res?.content || '';
      let parsed = null;
      try { parsed = JSON.parse(content.replace(/```json|```/g, '').trim()); } catch {}
      if (!parsed || typeof parsed !== 'object') throw new Error('AI returned an unexpected format');
      const clean = (arr, allow) => Array.isArray(arr)
        ? arr.filter((x) => typeof x === 'string').slice(0, 6).filter((x) => !allow || allow.includes(x))
        : [];
      set({
        ageRanges: clean(parsed.ageRanges, AGE_RANGES).length ? clean(parsed.ageRanges, AGE_RANGES) : t.ageRanges,
        jobTitles: clean(parsed.jobTitles).length ? clean(parsed.jobTitles) : t.jobTitles,
        industries: clean(parsed.industries).length ? clean(parsed.industries) : t.industries,
        interests: clean(parsed.interests).length ? clean(parsed.interests) : t.interests,
        cities: clean(parsed.cities).length ? clean(parsed.cities) : t.cities,
        educationLevel: clean(parsed.educationLevel, EDUCATION_LEVELS).length ? clean(parsed.educationLevel, EDUCATION_LEVELS) : t.educationLevel,
        experience: clean(parsed.experience, EXPERIENCE_BANDS).length ? clean(parsed.experience, EXPERIENCE_BANDS) : t.experience,
      });
      Alert.alert('✨ Applied', 'AI targeting suggestions applied — review and adjust below.');
    } catch (e) {
      Alert.alert('AI unavailable', e?.message || 'Could not get suggestions.');
    } finally { setAiBusy(false); }
  }, [campaign, set, t]);

  // ---------------------------------------------------------------------------
  // SAVE
  // ---------------------------------------------------------------------------

  const save = useCallback(async () => {
    if (!t.ageRanges?.length) { Alert.alert('Missing information', 'Select at least one age range.'); return; }
    if (!t.countries?.length) { Alert.alert('Missing information', 'Select at least one country.'); return; }
    setSaving(true);
    let savedToServer = false;
    let failDetail = '';
    try {
      if (!campaignId) {
        failDetail = 'No campaign id reached this screen — open Targeting from a campaign card/details so the id is passed.';
      } else {
        const h = await getH();
        const res = await fetch(`${API_BASE}/api/data/campaigns/${campaignId}`, {
          method: 'PUT', headers: h,
          body: JSON.stringify({ targeting: JSON.stringify(t) }),
        });
        const raw = await res.text().catch(() => '');
        let json = null;
        try { json = JSON.parse(raw); } catch {}
        savedToServer = res.ok && json?.success !== false;
        if (!savedToServer) failDetail = `HTTP ${res.status}: ${raw.slice(0, 200)}`;
      }
    } catch (e) { failDetail = e?.message || 'network error'; }
    // Local copy regardless
    if (campaignId) {
      AsyncStorage.setItem(`campaign_targeting_${campaignId}`, JSON.stringify(t)).catch(() => {});
    }
    setSaving(false);
    setDirty(false);
    const updatedCampaign = { ...campaign, targeting: t };
    Alert.alert(
      savedToServer ? '✅ Targeting saved' : '⚠️ Saved locally',
      savedToServer
        ? 'Audience targeting has been updated on the server.'
        : `Server rejected the update — targeting was kept on this device.\n\nReason: ${failDetail || 'unknown'}`,
      [{ text: 'OK', onPress: () => navigation.navigate('CampaignDetails', { campaign: updatedCampaign, campaignId }) }]
    );
  }, [t, campaign, navigation]);

  const resetDefaults = useCallback(() => {
    Alert.alert('Reset targeting', 'Restore the default audience settings?', [
      { text: 'Cancel', style: 'cancel' },
      { text: 'Reset', style: 'destructive', onPress: () => { setT({ ...DEFAULT_TARGETING }); setDirty(true); } },
    ]);
  }, []);

  // ---------------------------------------------------------------------------
  // RENDER
  // ---------------------------------------------------------------------------

  return (
    <SafeAreaView style={s.safe} edges={['top']}>
      {/* Header */}
      <View style={s.header}>
        <TouchableOpacity style={s.headerBtn} onPress={() => navigation.goBack()}>
          <Ionicons name="chevron-back" size={24} color="#fff" />
        </TouchableOpacity>
        <View style={{ flex: 1 }}>
          <Text style={s.headerTitle}>Audience Targeting{dirty ? ' •' : ''}</Text>
          <Text style={s.headerSub} numberOfLines={1}>{campaign?.name || 'Campaign'}</Text>
        </View>
        <DbBadge status={dbStatus} onPress={showDbDetail} />
        <View style={{ marginLeft: 8 }}>
          <CompanyLogo uri={companyLogo} size={28} />
        </View>
      </View>

      {/* Estimated reach */}
      <View style={s.reachBar}>
        <View style={{ flex: 1 }}>
          <Text style={s.reachLabel}>Estimated reach</Text>
          <View style={s.reachTrack}>
            <View style={[s.reachFill, { width: `${Math.max(4, estimate.ratio * 100)}%`, backgroundColor: estimate.band.color }]} />
          </View>
        </View>
        <View style={{ alignItems: 'flex-end', marginLeft: 12 }}>
          <Text style={s.reachValue}>{estimate.label}</Text>
          <Text style={[s.reachBand, { color: estimate.band.color }]}>{estimate.band.txt}</Text>
        </View>
      </View>

      <KeyboardAvoidingView style={{ flex: 1 }} behavior={Platform.OS === 'ios' ? 'padding' : undefined}>
        <ScrollView style={s.body} keyboardShouldPersistTaps="handled" showsVerticalScrollIndicator={false}>
          {/* AI suggest */}
          <TouchableOpacity style={[s.aiBtn, aiBusy && { opacity: 0.6 }]} onPress={aiSuggest} disabled={aiBusy}>
            {aiBusy ? <ActivityIndicator size="small" color="#fff" /> : <MaterialCommunityIcons name="brain" size={17} color="#fff" />}
            <Text style={s.aiBtnText}>{aiBusy ? 'Analysing campaign…' : 'AI suggest targeting from this campaign'}</Text>
          </TouchableOpacity>

          {/* Demographics */}
          <Section icon="account-group-outline" title="Demographics" count={t.ageRanges.length}>
            <Text style={s.fieldLabel}>Age ranges</Text>
            <View style={s.chipWrap}>
              {AGE_RANGES.map((a) => (
                <Chip key={a} label={a} active={t.ageRanges.includes(a)} onPress={() => toggleIn('ageRanges', a)} />
              ))}
            </View>
            <Text style={s.fieldLabel}>Gender</Text>
            <View style={s.chipWrap}>
              {GENDERS.map((g) => (
                <Chip key={g.id} label={g.label} active={t.gender === g.id} onPress={() => set({ gender: g.id })} />
              ))}
            </View>
          </Section>

          {/* Location */}
          <Section icon="map-marker-outline" title="Location" count={t.countries.length + t.cities.length}>
            <Text style={s.fieldLabel}>Countries</Text>
            <View style={s.chipWrap}>
              {COUNTRIES.map((cn) => (
                <Chip key={cn} label={cn} active={t.countries.includes(cn)} onPress={() => toggleIn('countries', cn)} />
              ))}
            </View>
            <Text style={s.fieldLabel}>Cities</Text>
            <View style={s.chipWrap}>
              {[...new Set([...UK_CITIES, ...t.cities])].map((city) => (
                <Chip key={city} label={city} active={t.cities.includes(city)} onPress={() => toggleIn('cities', city)} />
              ))}
            </View>
            <AddRow value={cityInput} onChangeText={setCityInput} onAdd={() => addTo('cities', cityInput, setCityInput)} placeholder="Add another city…" />
            <Stepper label="Radius around cities" value={t.radius} onChange={(v) => set({ radius: v })} />
          </Section>

          {/* Professional */}
          <Section icon="briefcase-outline" title="Professional" count={t.jobTitles.length + t.industries.length}>
            <Text style={s.fieldLabel}>Job titles</Text>
            <View style={s.chipWrap}>
              {t.jobTitles.map((j) => (
                <Chip key={j} label={j} active removable onRemove={() => removeFrom('jobTitles', j)} />
              ))}
            </View>
            <AddRow value={jobTitleInput} onChangeText={setJobTitleInput} onAdd={() => addTo('jobTitles', jobTitleInput, setJobTitleInput)} placeholder="Add job title…" />

            <Text style={s.fieldLabel}>Industries</Text>
            <View style={s.chipWrap}>
              {t.industries.map((i) => (
                <Chip key={i} label={i} active removable onRemove={() => removeFrom('industries', i)} />
              ))}
            </View>
            <AddRow value={industryInput} onChangeText={setIndustryInput} onAdd={() => addTo('industries', industryInput, setIndustryInput)} placeholder="Add industry…" />

            <Text style={s.fieldLabel}>Company size</Text>
            <View style={s.chipWrap}>
              {COMPANY_SIZES.map((cs) => (
                <Chip key={cs} label={cs} active={t.companySize.includes(cs)} onPress={() => toggleIn('companySize', cs)} />
              ))}
            </View>

            <Text style={s.fieldLabel}>Experience</Text>
            <View style={s.chipWrap}>
              {EXPERIENCE_BANDS.map((e) => (
                <Chip key={e} label={e} active={(t.experience || []).includes(e)} onPress={() => toggleIn('experience', e)} />
              ))}
            </View>

            <Text style={s.fieldLabel}>Salary band</Text>
            <View style={s.chipWrap}>
              {SALARY_BANDS.map((sb) => (
                <Chip key={sb} label={sb} active={(t.salaryBands || []).includes(sb)} onPress={() => toggleIn('salaryBands', sb)} />
              ))}
            </View>
          </Section>

          {/* Education */}
          <Section icon="school-outline" title="Education" count={t.educationLevel.length}>
            <Text style={s.fieldLabel}>Education level</Text>
            <View style={s.chipWrap}>
              {EDUCATION_LEVELS.map((el) => (
                <Chip key={el} label={el} active={t.educationLevel.includes(el)} onPress={() => toggleIn('educationLevel', el)} />
              ))}
            </View>
            <Text style={s.fieldLabel}>Field of study</Text>
            <View style={s.chipWrap}>
              {t.fieldOfStudy.map((f) => (
                <Chip key={f} label={f} active removable onRemove={() => removeFrom('fieldOfStudy', f)} />
              ))}
            </View>
            <AddRow value={fieldInput} onChangeText={setFieldInput} onAdd={() => addTo('fieldOfStudy', fieldInput, setFieldInput)} placeholder="Add field of study…" />
          </Section>

          {/* Interests */}
          <Section icon="heart-outline" title="Interests" count={t.interests.length}>
            <View style={s.chipWrap}>
              {[...new Set([...SUGGESTED_INTERESTS, ...t.interests])].map((i) => (
                <Chip key={i} label={i} active={t.interests.includes(i)} onPress={() => toggleIn('interests', i)} />
              ))}
            </View>
            <AddRow value={interestInput} onChangeText={setInterestInput} onAdd={() => addTo('interests', interestInput, setInterestInput)} placeholder="Add interest…" />
          </Section>

          {/* Advanced */}
          <Section icon="tune" title="Advanced" subtitle="Exclusions, languages, custom audiences">
            <Text style={s.fieldLabel}>Languages</Text>
            <View style={s.chipWrap}>
              {LANGUAGES.map((l) => (
                <Chip key={l} label={l} active={(t.languages || []).includes(l)} onPress={() => toggleIn('languages', l)} />
              ))}
            </View>

            <Text style={s.fieldLabel}>Exclude audiences</Text>
            <View style={s.chipWrap}>
              {(t.excludeAudience || []).map((x) => (
                <Chip key={x} label={x} active removable onRemove={() => removeFrom('excludeAudience', x)} />
              ))}
            </View>
            <AddRow value={excludeInput} onChangeText={setExcludeInput} onAdd={() => addTo('excludeAudience', excludeInput, setExcludeInput)} placeholder="e.g. Existing employees…" />

            <Toggle
              label="Custom audience"
              sub="Use your uploaded contact lists when available"
              value={!!t.customAudience}
              onChange={(v) => set({ customAudience: v })}
            />
          </Section>

          {/* Actions */}
          <View style={{ flexDirection: 'row', gap: 10, marginTop: 4 }}>
            <TouchableOpacity style={s.resetBtn} onPress={resetDefaults}>
              <Ionicons name="refresh" size={16} color={C.sub} />
              <Text style={s.resetText}>Reset</Text>
            </TouchableOpacity>
            <TouchableOpacity style={[s.saveBtn, saving && { opacity: 0.6 }]} onPress={save} disabled={saving}>
              {saving ? <ActivityIndicator size="small" color="#fff" /> : <Ionicons name="checkmark" size={18} color="#fff" />}
              <Text style={s.saveText}>Save Targeting</Text>
            </TouchableOpacity>
          </View>
          <View style={{ height: 40 }} />
        </ScrollView>
      </KeyboardAvoidingView>
    </SafeAreaView>
  );
};

// =============================================================================
// STYLES
// =============================================================================

const s = StyleSheet.create({
  safe: { flex: 1, backgroundColor: C.primary },
  header: { flexDirection: 'row', alignItems: 'center', backgroundColor: C.primary, paddingHorizontal: 10, paddingVertical: 10, gap: 6 },
  headerBtn: { padding: 5 },
  headerTitle: { color: '#fff', fontSize: 17, fontWeight: '800' },
  headerSub: { color: 'rgba(255,255,255,0.65)', fontSize: 11, marginTop: 1 },

  dbBadge: { flexDirection: 'row', alignItems: 'center', gap: 5, backgroundColor: 'rgba(255,255,255,0.14)', borderRadius: 12, paddingHorizontal: 9, paddingVertical: 5 },
  dbDot: { width: 7, height: 7, borderRadius: 4 },
  dbText: { color: '#fff', fontSize: 10.5, fontWeight: '700' },

  reachBar: { flexDirection: 'row', alignItems: 'center', backgroundColor: C.primaryDark, paddingHorizontal: 14, paddingVertical: 10 },
  reachLabel: { color: 'rgba(255,255,255,0.7)', fontSize: 10.5, fontWeight: '700', marginBottom: 5 },
  reachTrack: { height: 7, backgroundColor: 'rgba(255,255,255,0.15)', borderRadius: 4, overflow: 'hidden' },
  reachFill: { height: 7, borderRadius: 4 },
  reachValue: { color: '#fff', fontSize: 17, fontWeight: '800' },
  reachBand: { fontSize: 10, fontWeight: '800', marginTop: 1 },

  body: { flex: 1, backgroundColor: C.bg, paddingHorizontal: 14, paddingTop: 12 },

  aiBtn: { flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 7, backgroundColor: C.accent, borderRadius: 12, paddingVertical: 12, marginBottom: 4 },
  aiBtnText: { color: '#fff', fontSize: 13, fontWeight: '800' },

  section: { backgroundColor: C.card, borderRadius: 14, padding: 14, marginTop: 12, borderWidth: 1, borderColor: C.border },
  sectionHead: { flexDirection: 'row', alignItems: 'center', marginBottom: 6 },
  sectionIcon: { width: 32, height: 32, borderRadius: 10, backgroundColor: '#EFF4FF', alignItems: 'center', justifyContent: 'center' },
  sectionTitle: { fontSize: 14.5, fontWeight: '800', color: C.text },
  sectionSub: { fontSize: 11, color: C.sub, marginTop: 1 },
  countBadge: { minWidth: 24, height: 22, borderRadius: 11, backgroundColor: C.primary, alignItems: 'center', justifyContent: 'center', paddingHorizontal: 7 },
  countText: { color: '#fff', fontSize: 11, fontWeight: '800' },

  fieldLabel: { fontSize: 12, fontWeight: '700', color: C.text, marginTop: 12, marginBottom: 7 },

  chipWrap: { flexDirection: 'row', flexWrap: 'wrap', gap: 7 },
  chip: { flexDirection: 'row', alignItems: 'center', paddingHorizontal: 11, paddingVertical: 7, borderRadius: 15, backgroundColor: '#F8FAFD', borderWidth: 1, borderColor: C.border },
  chipActive: { backgroundColor: C.primary, borderColor: C.primary },
  chipText: { fontSize: 12, fontWeight: '600', color: C.sub },
  chipTextActive: { color: '#fff' },

  addRow: { flexDirection: 'row', alignItems: 'center', gap: 8, marginTop: 9 },
  addInput: { flex: 1, backgroundColor: '#F8FAFD', borderWidth: 1, borderColor: C.border, borderRadius: 10, paddingHorizontal: 12, paddingVertical: Platform.OS === 'ios' ? 9 : 7, fontSize: 13, color: C.text },
  addBtn: { width: 38, height: 38, borderRadius: 10, backgroundColor: C.primary, alignItems: 'center', justifyContent: 'center' },

  stepperRow: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginTop: 14 },
  stepperLabel: { fontSize: 13, fontWeight: '600', color: C.text },
  stepperControls: { flexDirection: 'row', alignItems: 'center', gap: 10 },
  stepperBtn: { width: 28, height: 28, borderRadius: 8, backgroundColor: '#EFF4FF', alignItems: 'center', justifyContent: 'center' },
  stepperValue: { fontSize: 13, fontWeight: '800', color: C.primary, minWidth: 58, textAlign: 'center' },

  toggleRow: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginTop: 14 },
  toggleSub: { fontSize: 11, color: C.sub, marginTop: 2 },
  toggleTrack: { width: 42, height: 24, borderRadius: 12, backgroundColor: '#CBD5E1', padding: 3 },
  toggleThumb: { width: 18, height: 18, borderRadius: 9, backgroundColor: '#fff' },

  resetBtn: { flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 5, backgroundColor: '#F1F5F9', borderRadius: 12, paddingVertical: 13, paddingHorizontal: 18 },
  resetText: { fontSize: 13, fontWeight: '700', color: C.sub },
  saveBtn: { flex: 1, flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 7, backgroundColor: C.success, borderRadius: 12, paddingVertical: 13 },
  saveText: { color: '#fff', fontSize: 14.5, fontWeight: '800' },
});

export default TargetingSettingsScreen;
